export const DEFAULT_BRAND = '#0f766e';

/** Negro o blanco, lo que se lea mejor sobre el color dado. */
export function contrastText(hex: string): '#000000' | '#ffffff' {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255) as [number, number, number];
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return luminance > 0.45 ? '#000000' : '#ffffff';
}

const STORAGE_KEY = 'pos-saas-brand';

/** Aplica el color del negocio a toda la interfaz (variables CSS). */
export function applyBrandColor(color: string | undefined): void {
  const brand = color && /^#[0-9a-f]{6}$/i.test(color) ? color : DEFAULT_BRAND;
  const root = document.documentElement;
  root.style.setProperty('--brand', brand);
  root.style.setProperty('--brand-fg', contrastText(brand));
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', brand);
  try {
    localStorage.setItem(STORAGE_KEY, brand);
  } catch {
    // almacenamiento no disponible: solo se pierde el color inicial
  }
}

/** Aplica el último color conocido antes del primer render (evita el parpadeo del color por defecto). */
export function applySavedBrandColor(): void {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) applyBrandColor(saved);
  } catch {
    // sin almacenamiento: queda el color por defecto
  }
}

// ---------------------------------------------------------------------------
// Modo claro / oscuro (preferencia de cada dispositivo)
// ---------------------------------------------------------------------------

export type ThemePreference = 'system' | 'light' | 'dark';

const THEME_KEY = 'pos-saas-theme';
const darkQuery = () => (typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null);
const listeners = new Set<() => void>();

export function getThemePreference(): ThemePreference {
  try {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    // sin almacenamiento: se sigue al dispositivo
  }
  return 'system';
}

/** Si ahora se ve oscuro (por elección o porque el dispositivo está en modo oscuro). */
export function isDarkNow(): boolean {
  const pref = getThemePreference();
  return pref === 'dark' || (pref === 'system' && !!darkQuery()?.matches);
}

function applyTheme(): void {
  const root = document.documentElement;
  root.dataset.theme = isDarkNow() ? 'dark' : 'light';
  // La paleta se aplica siempre: en modo claro tiñe la barra lateral (que es oscura).
  root.dataset.palette = getDarkPalette();
}

export function setThemePreference(pref: ThemePreference): void {
  try {
    if (pref === 'system') localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, pref);
  } catch {
    // sin almacenamiento: vale hasta recargar
  }
  applyTheme();
  listeners.forEach((l) => l());
}

// ---------------------------------------------------------------------------
// Colores del modo oscuro (preferencia de cada dispositivo; los define index.css)
// ---------------------------------------------------------------------------

export const DARK_PALETTES = [
  { key: 'pizarra', label: 'Pizarra', swatch: ['#0a0f1c', '#334155'] },
  { key: 'grafito', label: 'Grafito', swatch: ['#0e0e10', '#3f3f46'] },
  { key: 'negro', label: 'Negro', swatch: ['#000000', '#2e2e2e'] },
  { key: 'azul', label: 'Azul noche', swatch: ['#060c1d', '#233468'] },
  { key: 'bosque', label: 'Bosque', swatch: ['#08120f', '#254439'] },
  { key: 'cafe', label: 'Café', swatch: ['#110f0d', '#44403c'] },
] as const;

export type DarkPalette = (typeof DARK_PALETTES)[number]['key'];

const PALETTE_KEY = 'pos-saas-dark-palette';

export function getDarkPalette(): DarkPalette {
  try {
    const saved = localStorage.getItem(PALETTE_KEY);
    const found = DARK_PALETTES.find((p) => p.key === saved);
    if (found) return found.key;
  } catch {
    // sin almacenamiento: la de por defecto
  }
  return 'pizarra';
}

export function setDarkPalette(palette: DarkPalette): void {
  try {
    if (palette === 'pizarra') localStorage.removeItem(PALETTE_KEY);
    else localStorage.setItem(PALETTE_KEY, palette);
  } catch {
    // sin almacenamiento: vale hasta recargar
  }
  applyTheme();
  listeners.forEach((l) => l());
}

export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Antes del primer render; en "automático" sigue los cambios del dispositivo (p. ej. de noche). */
export function initTheme(): void {
  applyTheme();
  darkQuery()?.addEventListener('change', applyTheme);
}
